import type { LayeredSceneResponse } from "../types/layeredScene";

export interface FetchLayeredSceneOptions {
  /** Base URL of the deployment. Empty = same origin. */
  baseUrl?: string;
  /** Scene GUID (preferred) or slug. The server accepts both at the same route. */
  idOrSlug: string;
  /** DVar language id. Defaults server-side to 1 when omitted. */
  lang?: number;
  /**
   * Author-only flag. When true, asks the server to include unpublished materials
   * and non-Ready layers. Honored only for dashboard-authenticated requests; the
   * cookie attaches automatically because we fetch with credentials:"include".
   */
  includeUnpublished?: boolean;
  signal?: AbortSignal;
}

/**
 * Fetches a single scene plus all its surfaces and material choices in one call.
 *
 * Auth: anonymous callers (e.g. the visualizer at miltonhuse.3dconfig.dk) get the
 * published+Ready view. Dashboard previews pass includeUnpublished=true and the
 * same-origin cookie unlocks the unpublished view server-side.
 */
export async function fetchLayeredScene(opts: FetchLayeredSceneOptions): Promise<LayeredSceneResponse> {
  const params = new URLSearchParams();
  if (opts.lang !== undefined) params.set("lang", String(opts.lang));
  if (opts.includeUnpublished) params.set("includeUnpublished", "true");

  const base = (opts.baseUrl ?? "").replace(/\/+$/, "");
  const qs = params.toString();
  const url = `${base}/api/layered-scene/scenes/${encodeURIComponent(opts.idOrSlug)}${qs ? "?" + qs : ""}`;

  const res = await fetch(url, {
    headers: { Accept: "application/json" },
    credentials: "include",
    signal: opts.signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`fetchLayeredScene failed: ${res.status} ${res.statusText} ${text}`);
  }
  return (await res.json()) as LayeredSceneResponse;
}

/**
 * Builds the compose URL for the current set of picks. The browser fetches this
 * directly as an <img src>; the server returns image/jpeg. Layers whose status
 * isn't Ready are silently skipped by the compositor, so unpublished previews
 * just degrade gracefully — no client-side filtering needed.
 */
export function buildComposeUrl(opts: {
  baseUrl?: string;
  idOrSlug: string;
  /** collectionId → materialId */
  choices: Record<string, string>;
}): string {
  const base = (opts.baseUrl ?? "").replace(/\/+$/, "");
  const params = new URLSearchParams();
  for (const [collectionId, materialId] of Object.entries(opts.choices)) {
    params.set(collectionId, materialId);
  }
  return `${base}/api/layered-scene/scenes/${encodeURIComponent(opts.idOrSlug)}/compose?${params.toString()}`;
}
